import * as turf from '@turf/turf';
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';

import { inferCalibrationRegionFromRegionLabel } from '@/features/map/utils/floodCalibrationRegionInference';
import {
  type FloodCalibrationZoneRegion,
  getDepthScaleForYear,
} from '@/features/map/utils/vietnamFloodScenarioCalibration';
import type { EnhancedFloodPoint } from '@/types/flood.types';

import { buildEnhancedFloodPointFromDemoRegion } from './enhancedFloodDataGenerator';

/** Properties expected on region polygons (`public/hanoi_flood_areas.geojson`). */
export interface DemoRegionGeoJsonProperties {
  name: string;
  depth_m: number;
  area_km2?: number;
  affected_pop?: number;
  severity?: string;
  status?: string;
  /**
   * `north` | `central` | `south` | `highlands` — căn chỉnh theo kịch bản năm.
   * `depth_m` trong file là tham chiếu tĩnh; khi có `scenarioYear`, độ sâu hiển thị nhân thêm hệ số miền/năm.
   */
  calibration_region?: 'north' | 'central' | 'south' | 'highlands';
}

function resolveCalibrationRegion(props: DemoRegionGeoJsonProperties): FloodCalibrationZoneRegion {
  const r = props.calibration_region;
  if (r === 'north' || r === 'central' || r === 'south' || r === 'highlands') return r;
  return inferCalibrationRegionFromRegionLabel(props.name);
}

/** Điểm ngập bổ sung trong mỗi polygon (ngoài centroid) — tăng để gần thực tế quan trắc dày hơn. */
const DEFAULT_EXTRA_SAMPLES = 8;
const MAX_RANDOM_ATTEMPTS_PER_SAMPLE = 100;

export function inferProvinceFromRegionName(name: string): string {
  if (name.includes('Hà Nội')) return 'Hà Nội';
  if (name.includes('Hải Phòng')) return 'Hải Phòng';
  if (name.includes('Vĩnh Phúc')) return 'Vĩnh Phúc';
  if (name.includes('Thái Nguyên')) return 'Thái Nguyên';
  if (name.includes('Lạng Sơn')) return 'Lạng Sơn';
  if (name.includes('Sơn La')) return 'Sơn La';
  if (name.includes('Ninh Bình')) return 'Ninh Bình';
  if (name.includes('Thanh Hóa')) return 'Thanh Hóa';
  if (name.includes('Vinh')) return 'Nghệ An';
  if (name.includes('Quảng Bình')) return 'Quảng Bình';
  if (name.includes('Quảng Trị')) return 'Quảng Trị';
  if (name.includes('Huế') || name.includes('Thừa Thiên')) return 'Thừa Thiên Huế';
  if (name.includes('Đà Nẵng')) return 'Đà Nẵng';
  if (name.includes('Quảng Ngãi')) return 'Quảng Ngãi';
  if (name.includes('Quy Nhơn')) return 'Bình Định';
  if (name.includes('Nha Trang')) return 'Khánh Hòa';
  if (name.includes('Phan Thiết')) return 'Bình Thuận';
  if (name.includes('Đà Lạt')) return 'Lâm Đồng';
  if (name.includes('Buôn Ma Thuột')) return 'Đắk Lắk';
  if (name.includes('Pleiku')) return 'Gia Lai';
  if (name.includes('Biên Hòa')) return 'Đồng Nai';
  if (name.includes('Vũng Tàu')) return 'Bà Rịa – Vũng Tàu';
  if (name.includes('Mỹ Tho')) return 'Tiền Giang';
  if (name.includes('Long Xuyên')) return 'An Giang';
  if (name.includes('Rạch Giá')) return 'Kiên Giang';
  if (name.includes('Cần Thơ')) return 'Cần Thơ';
  if (name.includes('TP.HCM') || name.includes('Sài Gòn') || name.includes('Hồ Chí Minh'))
    return 'TP. Hồ Chí Minh';
  if (
    name.includes('Hoàn Kiếm') ||
    name.includes('Long Biên') ||
    name.includes('Trung Hoà') ||
    name.includes('Nhân Chính') ||
    name.includes('Kim Liên') ||
    name.includes('Láng')
  ) {
    return 'Hà Nội';
  }
  return 'Việt Nam';
}

export function inferDistrictLabel(name: string): string {
  if (name.includes('Sông Hồng')) return 'Lưu vực sông Hồng';
  if (name.includes('vùng trũng')) return 'Vùng trũng';
  if (name.includes('Sài Gòn') || name.includes('Nhiêu Lộc')) return 'Ven kênh đô thị';
  if (name.includes('sông Hàn')) return 'Ven sông Hàn';
  if (name.includes('Huế') || name.includes('Hương')) return 'Thành phố Huế';
  if (name.includes('Đà Nẵng')) return 'Nội thành Đà Nẵng';
  if (name.includes('Cần Thơ') || name.includes('ĐBSCL')) return 'Nội thành Cần Thơ';
  if (name.includes('ven sông') || name.includes('ven biển') || name.includes('đồng bằng'))
    return 'Khu vực trũng';
  if (name.includes('Tây Nguyên') || name.includes('thung lũng')) return 'Vùng trung tâm';
  if (name.includes('Trung Hoà') || name.includes('Nhân Chính')) return 'Trung Hoà – Nhân Chính';
  if (name.includes('Hoàn Kiếm')) return 'Hoàn Kiếm';
  if (name.includes('Kim Liên') || name.includes('Láng')) return 'Đống Đa';
  if (name.includes('Long Biên')) return 'Long Biên';
  return 'Khu vực cảnh báo';
}

/**
 * Chuyển FeatureCollection polygon vùng ngập thành danh sách điểm ngập (centroid + mẫu trong polygon)
 * đồng bộ với nhãn/polygon trên bản đồ.
 * Độ sâu (cm) áp dụng `getDepthScaleForYear` khi có `scenarioYear`.
 */
export function enhancedFloodPointsFromDemoRegionGeoJson(
  collection: FeatureCollection | null | undefined,
  options?: {
    extraSamplesPerPolygon?: number;
    scenarioYear?: number;
    referenceDateIso?: string | null;
  },
): EnhancedFloodPoint[] {
  if (!collection?.features?.length) return [];
  const extra = options?.extraSamplesPerPolygon ?? DEFAULT_EXTRA_SAMPLES;
  const scenarioYear = options?.scenarioYear;
  const referenceDateIso = options?.referenceDateIso ?? null;
  const out: EnhancedFloodPoint[] = [];

  collection.features.forEach((feature, idx) => {
    if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') return;
    const props = feature.properties as DemoRegionGeoJsonProperties | null;
    if (!props?.name || props.depth_m == null || Number.isNaN(Number(props.depth_m))) return;

    const depthMRaw = Number(props.depth_m);
    const calRegion = resolveCalibrationRegion(props);
    const depthScale = scenarioYear != null ? getDepthScaleForYear(scenarioYear, calRegion) : 1;
    const depthM = depthMRaw * depthScale;
    const areaKm2 = Math.max(0.01, Number(props.area_km2 ?? 1));
    const population = Math.max(1, Math.floor(Number(props.affected_pop ?? 1000)));

    const poly = feature as Feature<Polygon | MultiPolygon>;
    const c = turf.centroid(poly);
    const [lng, lat] = c.geometry.coordinates;
    const province = inferProvinceFromRegionName(props.name);
    const district = inferDistrictLabel(props.name);

    out.push(
      buildEnhancedFloodPointFromDemoRegion({
        id: `demo-region-${idx}-centroid`,
        name: props.name,
        position: [lng, lat],
        depthCm: Math.round(depthM * 100),
        areaKm2,
        population,
        provinceLabel: province,
        districtLabel: district,
        scenarioYear,
        referenceDateIso,
      }),
    );

    const box = turf.bbox(poly);
    let seed = idx * 7919 + 17;
    const nextRand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };

    let added = 0;
    let attempts = 0;
    while (added < extra && attempts < MAX_RANDOM_ATTEMPTS_PER_SAMPLE * extra) {
      attempts += 1;
      const rp = turf.randomPoint(1, { bbox: box });
      const pt = rp.features[0];
      if (!turf.booleanPointInPolygon(pt, poly)) continue;
      const [plng, plat] = pt.geometry.coordinates;
      const jitter = 0.85 + nextRand() * 0.15;
      const depthCm = Math.max(1, Math.round(depthM * 100 * jitter));
      out.push(
        buildEnhancedFloodPointFromDemoRegion({
          id: `demo-region-${idx}-sample-${added}`,
          name: `${props.name} — Quan trắc #${added + 1}`,
          position: [plng, plat],
          depthCm,
          areaKm2: Math.max(0.05, Math.round((areaKm2 / (extra + 2)) * 100) / 100),
          population: Math.max(50, Math.floor(population / (extra + 3))),
          provinceLabel: province,
          districtLabel: district,
          scenarioYear,
          referenceDateIso,
        }),
      );
      added += 1;
    }
  });

  return out;
}
